import type { FC, ReactNode } from 'react'

import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useMemo } from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDatacenterStore } from '@/components/datacenter/store'
import { useInventory } from '@/components/datacenter/inventory-context'
import { glass } from '@/components/datacenter/hud/primitives'
import CmsText from '@/components/datacenter/cms-text'

import {
    formatCapacity,
    formatInteger,
    formatPower,
    severityOf,
    summarizeRack,
    toCategory,
    toStatus
} from '@/lib/inventory'

type TourStep = {
    id: string
    eyebrow: string
    title: string
    body: string
    duration: number
    run: () => void
    featured?: ReactNode
}

const useTourSteps = (): TourStep[] => {
    const { inventory, stats, layout, rackById } = useInventory()
    const setFocus = useDatacenterStore(state => state.setFocus)
    const selectRack = useDatacenterStore(state => state.selectRack)
    const selectDevice = useDatacenterStore(state => state.selectDevice)

    return useMemo(() => {
        const steps: TourStep[] = []
        const tour = { fromTour: true }
        const racked = inventory.devices.filter(device => device.rack_id && rackById.has(device.rack_id))

        steps.push({
            id: 'overview',
            eyebrow: 'The hall',
            title: 'Bird\'s-eye view',
            body: `${formatInteger(stats.racks)} racks in ${layout.rows.length} rows draw ${formatPower(stats.powerKw)} of a ${formatPower(stats.capacityKw)} power budget.`,
            duration: 7,
            run: () => setFocus('overview', tour)
        })

        if (stats.byCategory.server) {
            steps.push({
                id: 'compute',
                eyebrow: 'Compute',
                title: 'Into the compute aisle',
                body: `${formatInteger(stats.byCategory.server)} compute devices, from rack servers to GPU training nodes, breathe cold air from this sealed aisle.`,
                duration: 8,
                run: () => setFocus('server', tour)
            })
        }

        const hungriest = layout.racks
            .map(rack => ({ rack, summary: summarizeRack(rack.rack, rack.devices) }))
            .sort((a, b) => b.summary.powerKw - a.summary.powerKw)[0]

        if (hungriest && hungriest.summary.powerKw > 0) {
            const rack = hungriest.rack.rack

            steps.push({
                id: 'hungriest',
                eyebrow: 'Hotspot',
                title: 'The hungriest rack',
                body: `It draws ${formatPower(hungriest.summary.powerKw)} across ${hungriest.summary.deviceCount} devices. Its door swings open so you can look inside.`,
                duration: 8.5,
                run: () => selectRack(rack.id, tour),
                featured: (
                    <span className='inline-flex items-center gap-2'>
                        <CmsText table='racks' id={rack.id} field='code' value={rack.code} className='font-mono text-white' />
                        {rack.name && <CmsText table='racks' id={rack.id} field='name' value={rack.name} className='text-slate-400' />}
                    </span>
                )
            })
        }

        if (stats.byCategory.network) {
            steps.push({
                id: 'network',
                eyebrow: 'Network',
                title: 'The network core',
                body: `${formatInteger(stats.byCategory.network)} network devices tie every row together: spine fabric, core routing, internet edge and security.`,
                duration: 8,
                run: () => setFocus('network', tour)
            })
        }

        const troubled = [...racked]
            .filter(device => severityOf(toStatus(device.status)) >= 2)
            .sort((a, b) => severityOf(toStatus(b.status)) - severityOf(toStatus(a.status)))[0]

        if (troubled && troubled.rack_id) {
            const rackId = troubled.rack_id

            steps.push({
                id: 'attention',
                eyebrow: 'Needs attention',
                title: 'A device is raising an alarm',
                body: 'Monitoring flagged this one. Its status light pulses so the on-site team can spot it from the aisle.',
                duration: 9,
                run: () => selectDevice(troubled.id, rackId, tour),
                featured: (
                    <span className='block'>
                        <CmsText table='devices' id={troubled.id} field='hostname' value={troubled.hostname} className='block font-mono text-white' />
                        {troubled.notes && <CmsText table='devices' id={troubled.id} field='notes' value={troubled.notes} className='mt-0.5 block text-slate-400' />}
                    </span>
                )
            })
        }

        if (stats.byCategory.storage) {
            steps.push({
                id: 'storage',
                eyebrow: 'Storage',
                title: 'The storage zone',
                body: `${formatInteger(stats.byCategory.storage)} storage systems hold ${formatCapacity(stats.storageTb)} across flash, disk, object and tape.`,
                duration: 8,
                run: () => setFocus('storage', tour)
            })
        }

        const largest = racked
            .filter(device => toCategory(device.category) === 'storage' && device.storage_tb !== null)
            .sort((a, b) => (b.storage_tb ?? 0) - (a.storage_tb ?? 0))[0]

        if (largest && largest.rack_id) {
            const rackId = largest.rack_id

            steps.push({
                id: 'largest',
                eyebrow: 'Capacity',
                title: 'The biggest system in the hall',
                body: `It holds ${formatCapacity(largest.storage_tb ?? 0)} on its own.`,
                duration: 9,
                run: () => selectDevice(largest.id, rackId, tour),
                featured: (
                    <span className='inline-flex flex-wrap items-center gap-2'>
                        <CmsText table='devices' id={largest.id} field='hostname' value={largest.hostname} className='font-mono text-white' />
                        {largest.model && <CmsText table='devices' id={largest.id} field='model' value={largest.model} className='text-slate-400' />}
                    </span>
                )
            })
        }

        steps.push({
            id: 'finish',
            eyebrow: 'Your turn',
            title: 'Explore on your own',
            body: 'Pick a zone on the left, click any rack to open it, or search the full inventory.',
            duration: 7,
            run: () => setFocus('overview', tour)
        })

        return steps
    }, [inventory.devices, stats, layout, rackById, setFocus, selectRack, selectDevice])
}

const Tour: FC = (): ReactNode => {
    const touring = useDatacenterStore(state => state.touring)
    const step = useDatacenterStore(state => state.tourStep)
    const setTourStep = useDatacenterStore(state => state.setTourStep)
    const stopTour = useDatacenterStore(state => state.stopTour)
    const steps = useTourSteps()
    const current = steps[step]

    useEffect(() => {
        if (!touring) return

        const active = steps[step]

        if (!active) {
            stopTour()

            return
        }

        active.run()

        const timer = window.setTimeout(() => {
            if (step + 1 < steps.length) setTourStep(step + 1)
            else stopTour()
        }, active.duration * 1000)

        return () => window.clearTimeout(timer)
    }, [touring, step, steps, setTourStep, stopTour])

    return (
        <AnimatePresence>
            {touring && current && (
                <motion.section
                    key='tour'
                    aria-live='polite'
                    aria-label='Guided tour'
                    className={cn(glass, 'pointer-events-auto absolute bottom-[136px] left-1/2 z-30 w-[min(560px,calc(100%-24px))] -translate-x-1/2 overflow-hidden md:bottom-24')}
                    initial={{ opacity: 0, y: 24, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 24, scale: 0.97 }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                >
                    <div className='h-0.5 w-full bg-white/5'>
                        <motion.div
                            key={current.id}
                            className='h-full bg-gradient-to-r from-cyan-300 to-violet-300'
                            initial={{ width: '0%' }}
                            animate={{ width: '100%' }}
                            transition={{ duration: current.duration, ease: 'linear' }}
                        />
                    </div>

                    <div className='flex items-start gap-4 p-4'>
                        <AnimatePresence mode='wait' initial={false}>
                            <motion.div
                                key={current.id}
                                className='min-w-0 flex-1'
                                initial={{ opacity: 0, x: 16 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -16 }}
                                transition={{ duration: 0.3 }}
                            >
                                <p className='text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-300'>
                                    <span>{current.eyebrow}</span>
                                    <span className='text-slate-500'> · {step + 1} of {steps.length}</span>
                                </p>
                                <h2 className='mt-1 font-heading text-lg font-semibold text-white'>
                                    {current.title}
                                </h2>
                                <p className='mt-1 text-sm leading-relaxed text-slate-300'>
                                    {current.body}
                                </p>
                                {current.featured && (
                                    <div className='mt-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-xs'>
                                        {current.featured}
                                    </div>
                                )}
                            </motion.div>
                        </AnimatePresence>

                        <div className='flex shrink-0 items-center gap-1'>
                            <button
                                type='button'
                                onClick={() => setTourStep(Math.max(0, step - 1))}
                                disabled={step === 0}
                                className='flex size-8 items-center justify-center rounded-lg text-slate-300 transition hover:bg-white/10 disabled:opacity-30'
                                aria-label='Previous stop'
                            >
                                <ChevronLeft className='size-4' aria-hidden='true' />
                            </button>
                            <button
                                type='button'
                                onClick={() => step + 1 < steps.length ? setTourStep(step + 1) : stopTour()}
                                className='flex size-8 items-center justify-center rounded-lg text-slate-300 transition hover:bg-white/10'
                                aria-label='Next stop'
                            >
                                <ChevronRight className='size-4' aria-hidden='true' />
                            </button>
                            <button
                                type='button'
                                onClick={stopTour}
                                className='flex size-8 items-center justify-center rounded-lg text-slate-300 transition hover:bg-white/10'
                                aria-label='End the tour'
                            >
                                <X className='size-4' aria-hidden='true' />
                            </button>
                        </div>
                    </div>
                </motion.section>
            )}
        </AnimatePresence>
    )
}

export default Tour
